import questionTypes from "@/util/consts/questionTypes"

// Frontend-agnostic helpers for splitting a question list into page-break sub-pages.
// Kept free of Vuex/Nuxt coupling so it can later move into `amosweb-frontends` and be
// reused by the booking flow (see docs/tickets/registration/registration-pagebreak-subpages-design.md, §10/#5).
//
// A placeholder has TWO distinct roles (design doc §0.1/§6) — do not conflate them:
//   1. Page break (this module): a top-level placeholder with the `pageBreak` flag splits the
//      list into sub-pages. These placeholders are CONSUMED here and never rendered.
//   2. In-page card split (QuestionMain.vue): any placeholder left inside an already-split
//      slice starts a new card within the page. That layer only ever sees non-break
//      placeholders, precisely because this module has already removed the page breaks.

// A page break is a placeholder flagged `pageBreak`. Breaks must be top-level (hierarchy
// `page > section > group > question`), so a break that carries a `block` reference is
// mis-authored and ignored as a boundary.
export function isPageBreak(question) {
    return question.questionType === questionTypes.TYPE_PLACEHOLDER
        && !!question.pageBreak
        && !question.block
}

// Nest block children into their parent block question's `children` and return the
// top-level list (block children removed). Operates on shallow clones so the caller's
// source objects (e.g. the Vuex store) are never mutated.
export function groupQuestions(questions) {
    const grouped = []
    const byUuid = {}

    for (const question of questions) {
        byUuid[question.uuid] = {...question}
    }

    for (const question of questions) {
        const node = byUuid[question.uuid]
        if (!question.block) {
            grouped.push(node)
        } else if (byUuid[question.block]) {
            if (!byUuid[question.block].children) {
                byUuid[question.block].children = []
            }
            byUuid[question.block].children.push(node)
        }
    }

    return grouped
}

// Split a question list into ordered sub-pages at visible page breaks.
//
// @param questions  flat list (top-level questions + group children mixed)
// @param isVisible  (question) => boolean — group-aware visibility predicate. For a
//                   participant flow it must already fold in per-person evaluation.
// @returns [{ index, questions, hidden }] — `questions` is the grouped slice for that
//          sub-page; `hidden` is true when none of its questions are visible.
//
// Behaviour:
//  - Only visible page breaks split (conditional breaks: a hidden break merges its
//    neighbours). Break placeholders themselves are boundaries and never rendered.
//  - Empty slices (leading / trailing / consecutive breaks) are dropped.
export function getQuestionSubPages(questions, isVisible) {
    if (!questions || !questions.length) return []

    const grouped = groupQuestions(questions)
    const slices = [[]]

    for (const question of grouped) {
        if (isPageBreak(question)) {
            if (isVisible(question)) slices.push([])
            continue
        }
        slices[slices.length - 1].push(question)
    }

    return slices
        .filter(slice => slice.length > 0)
        .map((slice, index) => ({
            index,
            questions: slice,
            hidden: !slice.some(isVisible)
        }))
}
